import { getSDKClient } from '../../lib/sdk-client.js';
import type { CLIDirective, AskUserQuestion } from '../../lib/directive-types.js';

// ---------------------------------------------------------------------------
// Generic eval workflow (no prompt-id)
// ---------------------------------------------------------------------------

export interface GenericEvalWorkflowResult {
  mode: 'generic';
  message: string;
  workflow: string[];
  formatReference: {
    evalConfig: {
      criteria: {
        name: string;
        description: string;
        evaluationParameter: string;
      }[];
    };
  };
  _directive: CLIDirective;
}

/**
 * Returns a generic eval workflow when no prompt-id is provided.
 * Guides the agent to upload a prompt first, then re-run with a prompt-id.
 */
export function buildGenericEvalWorkflow(): GenericEvalWorkflowResult {
  return {
    mode: 'generic',
    message: 'No prompt-id provided. Follow this sequence to create an evaluation:',
    workflow: [
      '1. mutagent explore --json — scan codebase for prompts',
      '2. mutagent prompts create ... --json — upload the target prompt',
      '3. Re-run: mutagent prompts evaluation create <prompt-id> --guided --json',
    ],
    formatReference: {
      evalConfig: {
        criteria: [{
          name: '<criterion_name>',
          description: '<rubric: what correct vs incorrect looks like>',
          evaluationParameter: '<must match a field in inputSchema or outputSchema>',
        }],
      },
    },
    _directive: {
      instruction: 'Ask the user which prompt they want to evaluate. If unclear, run mutagent explore --json first.',
      next: ['mutagent explore --json', 'mutagent prompts list --json'],
    },
  };
}

// ---------------------------------------------------------------------------
// Disambiguation helper
// ---------------------------------------------------------------------------

export interface DisambiguationResult {
  message: string;
  selected: { id: string; name: string };
  similar: { id: string; name: string }[];
  askUserQuestions: {
    question: string;
    options: string[];
  }[];
}

/**
 * Check for prompts with similar names and return disambiguation info if found.
 * Returns null when no similar prompts exist.
 */
export async function checkPromptDisambiguation(
  promptId: string,
  promptName: string,
): Promise<DisambiguationResult | null> {
  try {
    const client = await getSDKClient();
    const allPrompts = await client.listPrompts();
    const targetName = promptName.toLowerCase();
    const similar = allPrompts.filter(
      (p: { id: string | number; name: string }) =>
        String(p.id) !== promptId &&
        (p.name.toLowerCase().includes(targetName) || targetName.includes(p.name.toLowerCase())),
    );

    if (similar.length === 0) return null;

    return {
      message: `Found ${String(similar.length)} prompt(s) with similar names. Confirm this is the right one:`,
      selected: { id: promptId, name: promptName },
      similar: similar.map((p: { id: string | number; name: string }) => ({ id: String(p.id), name: p.name })),
      askUserQuestions: [{
        question: `You selected "${promptName}" (ID: ${promptId}). Is this correct, or did you mean one of these?`,
        options: [
          `${promptName} (ID: ${promptId}) — SELECTED`,
          ...similar.map((p: { id: string | number; name: string }) => `${p.name} (ID: ${String(p.id)})`),
        ],
      }],
    };
  } catch {
    // Non-critical — don't block the main workflow on listPrompts failure
    return null;
  }
}

interface FieldInfo {
  field: string;
  source: 'inputSchema' | 'outputSchema';
  fieldSchema?: { type?: string; description?: string };
}

export interface GuidedWorkflowResult {
  prompt: { id: string; name: string };
  inputSchema: { fields: string[] };
  outputSchema: { fields: string[] };
  workflow: {
    description: string;
    steps: { step: number; action: string; detail: string }[];
    criteriaTemplate: Record<string, unknown>;
    exampleCommand: string;
  };
  _directive: CLIDirective;
  disambiguation?: DisambiguationResult;
  _meta: { inputFields: string[]; outputFields: string[]; allFields: FieldInfo[] };
}

/**
 * Build a guided workflow object for a prompt's evaluation.
 * Fetches prompt schemas and optional dataset examples to construct
 * a structured workflow that agents use to collect rubrics from users.
 */
export async function buildGuidedWorkflow(promptId: string): Promise<GuidedWorkflowResult> {
  const client = await getSDKClient();
  const prompt = await client.getPrompt(promptId);

  const truncate = (s: string, max: number): string => s.length > max ? s.slice(0, max) + '...' : s;

  const outputProperties = prompt.outputSchema && typeof prompt.outputSchema === 'object'
    ? ((prompt.outputSchema as Record<string, unknown>).properties ?? {}) as Record<string, { type?: string; description?: string }>
    : {} as Record<string, { type?: string; description?: string }>;
  const inputProperties = prompt.inputSchema && typeof prompt.inputSchema === 'object'
    ? ((prompt.inputSchema as Record<string, unknown>).properties ?? {}) as Record<string, { type?: string; description?: string }>
    : {} as Record<string, { type?: string; description?: string }>;

  const inputFields = Object.keys(inputProperties);
  const outputFields = Object.keys(outputProperties);

  const allFields: FieldInfo[] = [
    ...inputFields.map(f => ({ field: f, source: 'inputSchema' as const, fieldSchema: inputProperties[f] })),
    ...outputFields.map(f => ({ field: f, source: 'outputSchema' as const, fieldSchema: outputProperties[f] })),
  ];

  // Fetch a dataset example if available (non-critical)
  let datasetExample: Record<string, unknown> | null = null;
  try {
    const datasets = await client.listDatasets(promptId);
    if (datasets.length > 0) {
      try {
        const dsId = datasets[0]?.id;
        const items = dsId != null ? await client.listDatasetItems(String(dsId)) : [];
        if (items.length > 0) {
          const firstItem = items[0];
          datasetExample = ((firstItem?.expectedOutput ?? firstItem?.input) ?? null) as Record<string, unknown> | null;
        }
      } catch {
        // listDatasetItems may not exist yet — non-critical
      }
    }
  } catch { /* non-critical */ }

  const askUserQuestions: AskUserQuestion[] = allFields.map(({ field, source, fieldSchema }) => {
    const isInput = source === 'inputSchema';
    const question = isInput
      ? `Define the Minimum Viable Context for "${field}". What data MUST be present in this input for the prompt to produce a correct output? Describe what constitutes complete vs incomplete input, and WHY this field matters.`
      : `Define what correct "${field}" looks like. What structure, content, or qualities make it good vs bad? Give concrete examples of good and bad outputs.`;
    const hint = isInput
      ? `Input fields define what data the prompt NEEDS to work correctly. Without defining minimum viable context, the optimizer cannot detect whether failures come from bad input or bad prompt logic.`
      : undefined;
    const header = isInput ? `${field} [INPUT]` : `${field} [OUTPUT]`;
    const defineDesc = isInput
      ? `Describe what data MUST be present in "${field}" and WHY the prompt needs it. Focus on completeness and minimum viable context.`
      : `Describe what a correct vs incorrect "${field}" looks like. Focus on structure, content quality, and concrete examples — not numeric scores.`;

    return {
      field,
      source,
      question,
      header,
      ...(hint != null ? { hint } : {}),
      options: [
        {
          label: 'Define rubric',
          description: defineDesc,
        },
        {
          label: 'See suggestion',
          description: `Get a suggested rubric based on the prompt and schema definition for "${field}". You can refine it.`,
        },
      ],
      multiSelect: false,
      context: {
        fieldType: fieldSchema?.type ?? 'unknown',
        fieldDescription: fieldSchema?.description ?? null,
        fieldSource: source,
        promptExcerpt: truncate(prompt.humanPrompt ?? prompt.systemPrompt ?? prompt.rawPrompt ?? '', 200),
        exampleValue: datasetExample?.[field] ?? null,
      },
    };
  });

  const result: GuidedWorkflowResult = {
    prompt: { id: promptId, name: prompt.name },
    inputSchema: { fields: inputFields },
    outputSchema: { fields: outputFields },
    workflow: {
      description: 'Follow these steps to create an evaluation for this prompt:',
      steps: [
        { step: 1, action: 'Review prompt schemas and context', detail: 'Understand what the prompt does and what each field means.' },
        { step: 2, action: 'Define correctness criteria for EVERY field', detail: `For EACH of the ${String(allFields.length)} fields in _directive.askUserQuestions, use AskUserQuestion to collect a rubric. INPUT fields (${String(inputFields.length)}): collect minimum viable context definitions — what data MUST exist. OUTPUT fields (${String(outputFields.length)}): collect correctness criteria — what correct vs incorrect looks like. Do NOT skip any field. Use the context hints to suggest rubrics when asked.` },
        { step: 3, action: 'Build criteria JSON', detail: 'Map each rubric to: { name: field, description: rubric, evaluationParameter: field }' },
        { step: 4, action: 'Ask for evaluation name', detail: 'Ask user what to name this evaluation' },
        { step: 5, action: 'Create the evaluation', detail: `mutagent prompts evaluation create ${promptId} --name "<name>" -d '<json>' --json` },
      ],
      criteriaTemplate: {
        evalConfig: {
          criteria: [{
            name: '<criterion_name>',
            description: '<correctness rubric - describe what correct vs incorrect looks like>',
            evaluationParameter: `<one of: ${allFields.length > 0 ? allFields.map(f => f.field).join(', ') : 'no fields detected - check prompt schemas'}>`,
          }],
        },
      },
      exampleCommand: `mutagent prompts evaluation create ${promptId} --name "My Evaluation" -d '${JSON.stringify({
        evalConfig: {
          criteria: outputFields.slice(0, 1).map(f => ({
            name: `${f} accuracy`,
            description: `Correct if ${f} matches expected structure and content; incorrect if missing, malformed, or off-topic`,
            evaluationParameter: f,
          })),
        },
      })}'`,
    },
    _directive: {
      instruction:
        'MANDATORY ORDERS — READ CAREFULLY:\n' +

        `1. You MUST ask the user about EVERY SINGLE field listed in _directive.askUserQuestions. There are ${String(allFields.length)} fields total.\n` +
        '2. Do NOT skip ANY field. Do NOT decide that a field "doesn\'t need" a definition.\n' +
        `3. INPUT fields (${String(inputFields.length)}): These define MINIMUM VIABLE CONTEXT — the data that MUST exist in the input for the prompt to work. Ask: "What data MUST be present in this field and WHY does the prompt need it?"\n` +
        '   WHY THIS MATTERS: Without minimum viable context definitions, the optimizer cannot distinguish between failures caused by incomplete input vs failures caused by bad prompt logic.\n' +
        `4. OUTPUT fields (${String(outputFields.length)}): These define correctness criteria. Ask: "What does correct vs incorrect for this field look like?"\n` +
        `5. After collecting ALL ${String(allFields.length)} definitions, construct the --data JSON with criteria for EVERY field.\n` +
        '6. VIOLATION: Skipping ANY field or telling the user a field doesn\'t need a definition is a protocol violation.\n' +
        '\n' +
        '7. EVAL CRITERIA FRAMING — INPUT vs OUTPUT scoping (mirror of SKILL.md → "Guided Eval Criteria"):\n' +
        '   INPUT fields → Minimum Viable Context (MVC).\n' +
        '     Ask: "Is the data required for the prompt to succeed actually present in this {variable}?"\n' +
        '     Rubric uses a COMPLETENESS scale grounded in observable presence of required data:\n' +
        '       1.0 = all required context present, no ambiguity\n' +
        '       0.5 = some context present, enough to attempt but likely partial/hedged answer\n' +
        '       0.0 = critical context missing, prompt cannot succeed regardless of model\n' +
        '   OUTPUT fields → Output Standards.\n' +
        '     Ask: "Does the response meet content correctness + structural correctness?"\n' +
        '     Rubric uses a CORRECTNESS scale with concrete pass/fail tiers, e.g.:\n' +
        '       1.0 = valid shape, all required fields, faithful content\n' +
        '       0.5 = valid shape, 1-2 fields hedged or partially correct\n' +
        '       0.0 = invalid shape OR fabricated facts\n' +
        '   NEVER use vague rubrics like "0-1 scale" or "score based on accuracy".\n' +
        '   Canonical source: .claude/skills/mutagent-cli/SKILL.md → "Guided Eval Criteria".\n' +
        '   Note: template variables use single-brace `{variable}` (platform canonical). `mutagent explore`\n' +
        '   infers single vs double per-file via inferPromptVariables().',
      next: [
        `mutagent prompts evaluation create ${promptId} --name "<name>" -d '<criteria-json>' --json`,
      ],
      decisionTree: {
        step1: 'Check if criteria already exist in the user\'s code. If criteria match expected shape (name + description + evaluationParameter targeting schema fields), use --data directly.',
        step2: 'If criteria are missing or malformed, use the _directive.askUserQuestions payloads to collect them via AskUserQuestion.',
        step3: 'After collecting all criteria, construct the --data JSON and run: mutagent prompts evaluation create <prompt-id> --name "<name>" -d \'<json>\'',
      },
      askUserQuestions,
    },
    _meta: { inputFields, outputFields, allFields },
  };

  // Attach disambiguation info if similar prompts exist (non-critical)
  const disambiguation = await checkPromptDisambiguation(promptId, prompt.name);
  if (disambiguation) {
    result.disambiguation = disambiguation;
  }

  return result;
}
